import React from 'react';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Heading5 } from '~/components/atom/Typography';
import Textarea from '~/components/molecule/Textarea';
import DateTimePicker from '~/components/organism/DateTimePicker';
import type { ValidationError } from '~/components/page/LFA/state/validationState';
import type { Date__Input } from '~/graphql/types';
import { formatToYYYYMMDD } from '~/util/date';
import ErrorBlock from '../../../ErrorBlock';
import hasValue from '~/util/hasValue';

const text = {
  textarea: {
    label:
      'Voeg een notitie toe aan de herinnering. Dit helpt om later op te volgen.',
    placeholder: 'Voer een aangepaste reden in',
  },
  snoozeHeading: (
    <>
      Je bent nog in gesprek met een potentiële klant. Selecteer de datum waarop
      je de status kan updaten. <br />
      Wij zullen je helpen herinneren.
    </>
  ),
};

export type Props = {
  dataTestId?: string;
  className?: string;
  error?: ValidationError;
  snoozeUntil: Date__Input | null;
  snoozeNote: string;
  setSnoozeUntil: (snoozeUntil: Date__Input) => void;
  setSnoozeNote: (snoozeNote: string) => void;
};

const SnoozeBlock: React.FCC<Props> = ({
  dataTestId,
  className,
  snoozeUntil,
  setSnoozeUntil,
  snoozeNote,
  setSnoozeNote,
  error,
}) => (
  <JustificationContainer
    dataTestId={dataTestId}
    className={className}
    direction="column"
    border={{
      radius: 'base',
      color: { group: error ? 'danger' : 'primary' },
      width: 's',
    }}
    padding={['base']}
    gap="base"
    backgroundColor={{
      group: error ? 'danger' : 'tertiary',
      variant: 'translucent',
    }}
    width="100%"
  >
    <JustificationContainer direction="column" gap="xs" width="100%">
      <Heading5 required>{text.snoozeHeading}</Heading5>
      {error && <ErrorBlock error={error} />}
      <DateTimePicker
        dateProps={{
          date: snoozeUntil?.Day?.d || '',
          onDateChange: date => {
            const formattedDate = formatToYYYYMMDD(date);

            if (formattedDate) {
              const definedTime = snoozeUntil?.DayTime?.t;
              const newDate = definedTime
                ? { DayTime: { d: formattedDate, t: definedTime } }
                : { Day: { d: formattedDate } };

              setSnoozeUntil(newDate);
            }
          },
        }}
        timeProps={{
          time: snoozeUntil?.DayTime?.t || '',
          onTimeChange: time => {
            const newDate = hasValue(time)
              ? {
                  DayTime: {
                    d: snoozeUntil?.Day?.d || snoozeUntil?.DayTime?.d || '',
                    t: hasValue(time) ? `${time}:00` : '',
                  },
                }
              : {
                  Day: {
                    d: snoozeUntil?.Day?.d || snoozeUntil?.DayTime?.d || '',
                  },
                };
            setSnoozeUntil(newDate);
          },
        }}
      />
    </JustificationContainer>

    <JustificationContainer direction="column" gap="xs" width="100%">
      <Textarea
        label={text.textarea.label}
        onChange={value => setSnoozeNote(value || '')}
        placeholder={text.textarea.placeholder}
        name="note"
        value={snoozeNote}
        required
      />
    </JustificationContainer>
  </JustificationContainer>
);

export default SnoozeBlock;
